import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, Copy, Shield, User, Calendar, KeyRound, ArrowLeft, Sparkles } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import PricingPlans from '../Auth/PricingPlans';
import '../Auth/login.css';

const API = process.env.REACT_APP_BACKEND_URL;

export default function UserSettings() {
  const { user, token, isAdmin, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);

  // Display-name edit
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);

  // Demo vs paid heuristic: paid plans always extend at least 30 days
  // beyond the subscription start. The free demo is exactly 15 days.
  // If the subscription window is ≤ 16 days, treat the user as a demo
  // user and surface Plans & Pricing so they can upgrade.
  const subStart = user?.subscriptionStart ? new Date(user.subscriptionStart) : null;
  const subEnd   = user?.subscriptionEnd   ? new Date(user.subscriptionEnd)   : null;
  const subDays  = (subStart && subEnd) ? Math.round((subEnd - subStart) / (1000 * 60 * 60 * 24)) : 0;
  const isPaidUser = isAdmin || subDays > 16;

  const saveName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) { toast.error('Name cannot be empty'); return; }
    setSavingName(true);
    try {
      const res = await fetch(`${API}/api/auth/update-profile`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed }),
      });
      const data = await res.json();
      if (res.ok) {
        // Optimistic refresh of the auth context if available.
        if (setUser) setUser(data); else if (data) { /* eslint-disable-next-line no-self-assign */ user.name = data.name; }
        toast.success('Name updated');
        setEditingName(false);
      } else {
        toast.error(data.detail || 'Failed to update name');
      }
    } catch { toast.error('Network error'); }
    setSavingName(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Password changed successfully');
        setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      } else {
        toast.error(data.detail || 'Failed to change password');
      }
    } catch { toast.error('Network error'); }
    setLoading(false);
  };

  const copySerial = () => {
    navigator.clipboard.writeText(user?.serial || '');
    toast.success('Serial number copied');
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white" style={{ overflowY: 'auto', maxHeight: '100vh' }} data-testid="user-settings">
      <div className="max-w-2xl mx-auto p-6">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-zinc-400 hover:text-white" data-testid="back-to-app-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>

        {/* Account Info */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            {isAdmin ? <Shield className="w-5 h-5 text-violet-400" /> : <User className="w-5 h-5 text-zinc-400" />}
            Account Information
          </h2>
          <div className="grid gap-3 text-sm">
            <div className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg gap-3">
              <span className="text-zinc-400">Name</span>
              {editingName ? (
                <div className="flex items-center gap-2 flex-1 justify-end">
                  <Input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    autoFocus
                    className="bg-zinc-900 border-zinc-700 max-w-[220px]"
                    data-testid="settings-name-input"
                  />
                  <Button
                    size="sm" onClick={saveName} disabled={savingName}
                    className="bg-violet-600 hover:bg-violet-700"
                    data-testid="settings-name-save"
                  >{savingName ? 'Saving…' : 'Save'}</Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => { setEditingName(false); setNameDraft(user?.name || ''); }}
                    data-testid="settings-name-cancel"
                  >Cancel</Button>
                </div>
              ) : (
                <span className="flex items-center gap-2">
                  <span data-testid="settings-name-display">{user?.name}</span>
                  <Button
                    size="sm" variant="ghost" className="text-zinc-400 hover:text-white"
                    onClick={() => { setNameDraft(user?.name || ''); setEditingName(true); }}
                    data-testid="settings-name-edit"
                  >Edit</Button>
                </span>
              )}
            </div>
            <div className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
              <span className="text-zinc-400">Email</span>
              <span>{user?.email}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
              <span className="text-zinc-400">Role</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${isAdmin ? 'bg-violet-500/20 text-violet-300' : 'bg-zinc-700 text-zinc-300'}`}>
                {user?.role}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
              <span className="text-zinc-400">Subscription</span>
              <span className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-zinc-500" />
                {user?.subscriptionEnd ? new Date(user.subscriptionEnd).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Serial Number (Help Section) */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6" data-testid="serial-section">
          <h2 className="text-lg font-semibold mb-3">License Serial</h2>
          <div className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg font-mono text-sm">
            <span className="flex-1 text-zinc-300 select-all" data-testid="serial-number">{user?.serial || 'N/A'}</span>
            <Button variant="ghost" size="sm" onClick={copySerial} className="text-zinc-400 hover:text-white" data-testid="copy-serial-btn">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-zinc-500 text-xs mt-2">This is your unique license identifier. Keep it safe.</p>
        </div>

        {/* Change Password */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6" data-testid="change-password-section">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-400" /> Change Password
          </h2>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm">Current Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type={showPasswords ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                  required
                  data-testid="current-password-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type={showPasswords ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                  required
                  data-testid="new-password-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Confirm New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type={showPasswords ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                  required
                  data-testid="confirm-new-password-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <input type="checkbox" checked={showPasswords} onChange={(e) => setShowPasswords(e.target.checked)} className="rounded" />
              <span>Show passwords</span>
            </div>
            <Button type="submit" disabled={loading} className="bg-violet-600 hover:bg-violet-700" data-testid="change-password-btn">
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </div>

        {/* Plans & Pricing — surfaced ONLY for demo users so they can
            upgrade. Paid users (sub window > 16 days) and admins already
            have an active plan and don't need to see pricing here. */}
        {!isPaidUser && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6" data-testid="settings-pricing">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-fuchsia-300" /> Plans &amp; Pricing
              </h2>
              <Link to="/purchase" className="text-xs text-cyan-300 hover:text-cyan-200 transition-colors tracking-wider whitespace-nowrap"
                    data-testid="settings-see-full-plans">
                See full plans →
              </Link>
            </div>
            <p className="text-zinc-400 text-xs mb-4">
              Every account starts with a 15-day free demo. Upgrade any time to keep all features active.
            </p>
            <PricingPlans />
          </div>
        )}

        {/* Logout */}
        <Button variant="outline" onClick={logout} className="border-red-500/30 text-red-400 hover:bg-red-500/10 w-full" data-testid="logout-btn">
          Sign Out
        </Button>
      </div>
    </div>
  );
}
